import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/ui/docs-site/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
/*-- dri: Adam Locke --*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Drop rules";
import {Mermaid} from "@theguild/remark-mermaid/mermaid";
const frontMatter = {
  "title": "Drop rules"
};
import {Alert, Icon, Tabs} from '@/components';
import {mdiDelete, mdiShape, mdiShieldAccount} from '@mdi/js';
import ApiAuthReq, {useTOC as useTOC0} from '@/_partials/api-auth-req.mdx';
import InfoTerraformLockout, {useTOC as useTOC1} from '@/_partials/info-terraformlockout.mdx';
import TerraformPrereq, {useTOC as useTOC2} from '@/_partials/terraformprereq.mdx';
import TerraformCreate, {useTOC as useTOC3} from '@/_partials/terraformcreate.mdx';
export function useTOC(props) {
  return [{
    value: "Define a value-based drop rule",
    id: "define-a-value-based-drop-rule",
    depth: 2
  }, {
    value: "View and filter created drop rules",
    id: "view-and-filter-created-drop-rules",
    depth: 2
  }, ...useTOC0(), {
    value: "Create a drop rule",
    id: "create-a-drop-rule",
    depth: 2
  }, {
    value: "Matching with glob syntax",
    id: "matching-with-glob-syntax",
    depth: 3
  }, ...useTOC1(), ...useTOC3(), ...useTOC0(), {
    value: "Disable a drop rule",
    id: "disable-a-drop-rule",
    depth: 2
  }, ...useTOC1(), ...useTOC0(), {
    value: "Delete a drop rule",
    id: "delete-a-drop-rule",
    depth: 2
  }, ...useTOC0()];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    li: "li",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  if (!Tabs) _missingMdxReference("Tabs", false);
  if (!Tabs.Tab) _missingMdxReference("Tabs.Tab", true);
  return <>{}{"\n"}<_components.h1>{"Drop rules"}</_components.h1>{"\n"}<_components.p>{"Based on your amount of available storage and rate limits, you might want to reduce\nthe amount of metrics that you continue to store so that you’re keeping only those\nmetrics you need. For example, using "}<_components.a href="/investigate/analyze/usage">{"Telemetry Usage Analyzer"}</_components.a>{",\nyou might find a metric has a low utilization score. Metrics with\n"}<_components.a href="/investigate/analyze/usage#high-volume-low-utility-metrics">{"high volume and low utilization"}</_components.a>{"\nare candidates for dropping."}</_components.p>{"\n"}<_components.p>{"Use drop rules to omit incoming metrics based on labels, and have Chronosphere\nObservability Platform persist only those metrics you need. Define filters to specify\nthe metric or label names you want to drop. You can also drop metrics based on a\ndata point value."}</_components.p>{"\n"}<_components.p>{"The Collector scrapes data from an app, and sends the data to Chronosphere where you\ncan apply drop rules. Any metrics that weren’t impacted by drop rules are then\naggregated in Observability Platform."}</_components.p>{"\n"}<Mermaid chart="graph LR\n  accTitle: Drop rules data flowchart\n  accDescr: Where drop rules take effect in the flow of data into Observability Platform.\n  col(Collector) -- Scrapes --> app(Application)\n  col -- Sends --> Drop(&quot;Drop rules&quot;)\n  subgraph OP[&quot;Observability Platform&quot;]\n  Drop -- Metrics not dropped --> ca(Aggregate metrics)\n  end" />{"\n"}<_components.p>{"Drop rules affect data from the point of rule creation onward. When you query for the\nmetric:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Data can display in autocomplete searches for a period of approximately two hours."}</_components.li>{"\n"}<_components.li>{"Queries covering the time period before the drop rule took effect display the\nmetric until the "}<_components.a href="/administer/limits-licensing/licensing#ingestion-limits-and-retention-policies">{"retention period"}</_components.a>{" expires."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.p>{"Use the "}<_components.code>{"value_based_drop"}</_components.code>{" object to define a value-based drop rule. The\n"}<_components.code>{"target_drop_value"}</_components.code>{" parameter specifies the target data point value at which to drop\nmetrics."}</_components.p>{"\n"}<Alert severity="warning"><_components.p>{"The "}<_components.code>{"value_based_drop"}</_components.code>{" parameter drops time series based on data point values of "}<_components.code>{"0"}</_components.code>{"\nor "}<_components.code>{"1"}</_components.code>{". This type of drop rule can significantly reduce data points per second, but\nchanges metrics from continuous series to sparse metrics. Adding a rule of this type\ncan change the behavior of existing monitors or dashboards that use the target\nmetric."}</_components.p></Alert>{"\n"}<_components.p>{"You can include the "}<_components.code>{"drop_nan_value"}</_components.code>{" parameter to drop data point values that are Not\na Number (NaN). If set to "}<_components.code>{"true"}</_components.code>{", Chronosphere drops NaN data points, along with any\npublished\n"}<_components.a href="/ingest/metrics-traces/collector/configure/optimizations#enable-staleness-markers">{"staleness markers"}</_components.a>{"."}</_components.p>{"\n"}<_components.p>{"If you set the "}<_components.code>{"drop_nan_value"}</_components.code>{" parameter to "}<_components.code>{"true"}</_components.code>{" in conjunction with the\n"}<_components.code>{"value_based_drop"}</_components.code>{" parameter, Observability Platform drops data points matching the\nvalue "}<_components.em>{"or"}</_components.em>{" any NaN values."}</_components.p>{"\n"}<_components.p>{"If you want to drop "}<_components.em>{"only"}</_components.em>{" NaN values, set "}<_components.code>{"drop_nan_value"}</_components.code>{" to "}<_components.code>{"true"}</_components.code>{" without a\ncorresponding "}<_components.code>{"value_based_drop"}</_components.code>{" parameter. When no value is set for\n"}<_components.code>{"drop_nan_value"}</_components.code>{", all values are dropped, including NaN values."}</_components.p>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"To view the drop rules defined on your instance, you must have administrative\nprivileges."}</_components.p>{"\n"}<_components.p>{"Select from the following methods to view your available drop rules."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="view-drop-rules-ui"><_components.p>{"To view drop rules:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"In the navigation menu, click "}<_components.strong><Icon path={mdiShieldAccount} />{" Go to Admin"}</_components.strong>{" and\nthen select "}<_components.strong><Icon path={mdiShape} />{" Control "}<span aria-label="and then">{">"}</span>{" Drop Rules"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Use the "}<_components.strong>{"Search rules"}</_components.strong>{" text box to find a specific rule. This search looks for\nany text in any rule."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Select a value from the "}<_components.strong>{"Status"}</_components.strong>{" menu to filter for rules matching the selected\nstatus."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"For information about viewing, copying, or downloading rule configurations, see\n"}<_components.a href="/control/shaping/reduce-cardinality/review-aggregation-rules#rule-configuration">{"Rule configuration"}</_components.a>{"."}</_components.p></Tabs.Tab><Tabs.Tab label="API" id="view-drop-rule-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/ListDropRules"><_components.code>{"ListDropRules"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[2].id}>{toc[2].value}</_components.h2>{"\n"}<_components.p>{"Use drop rules to drop the ingestion of certain metrics that you don’t need to\npersist. For example, dropping metrics from a specific cluster, or test data."}</_components.p>{"\n"}<_components.p>{"Drop rules can have multiple filter conditions on different labels, making it\npossible to drop a subset of the series matching a particular metric name. Use\nmultiple filters for cases where only a subset of a metric’s data is used. For\nexample, if a metric is used in an alert but examines only series related to error\nstatuses, you can drop non-error status series."}</_components.p>{"\n"}<_components.p>{"Drop rules have these definition limitations:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"The rule can’t be longer than 4,096 characters."}</_components.li>{"\n"}<_components.li>{"A drop rule slug must contain only alphanumeric\ncharacters, dashes, or underscores.\nThe allowed regular expression for matching is "}<_components.code>{"[a-zA-Z0-9-_]"}</_components.code>{". Spaces aren’t\nallowed."}</_components.li>{"\n"}<_components.li>{"Each rule name must be unique."}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.h3 id={toc[3].id}>{toc[3].value}</_components.h3>{"\n"}<_components.p>{"Verify your "}<_components.a href="/investigate/querying/glob-syntax">{"glob syntax"}</_components.a>{" with\n"}<_components.a href="/investigate/analyze/telemetry-analyzer">{"Live Telemetry Analyzer"}</_components.a>{" to ensure your query\nmatches the correct metrics. For example, the following drop rule for the\n"}<_components.a href="/tooling/api-info/definition/operations/CreateDropRule">{"CreateDropRule API"}</_components.a>{" drops any\nmetric containing the "}<_components.code>{"service"}</_components.code>{" label with a value of "}<_components.code>{"api"}</_components.code>{" that also has a "}<_components.code>{"cluster"}</_components.code>{"\nlabel that begins with "}<_components.code>{"dev"}</_components.code>{"."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"  \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"drop_rule"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api-drop-rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api drop rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"mode"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"filters"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"service\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"cluster\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"dev*\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    ]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"To keep only metrics with a specific label value, use glob syntax to specify a “does not\nequals” match with an exclamation mark "}<_components.code>{"!"}</_components.code>{" to match specified values. For example, the\nfollowing drop rule drops any metric containing the "}<_components.code>{"service"}</_components.code>{" label with a value of "}<_components.code>{"api"}</_components.code>{",\nthat doesn’t also have an "}<_components.code>{"env"}</_components.code>{" label with a value of "}<_components.code>{"production"}</_components.code>{". This drop rule also\napplies if the "}<_components.code>{"env"}</_components.code>{" label isn’t set."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"  \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"drop_rule"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api-drop-rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api drop rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"mode"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"filters"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"service\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"env!\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"production\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    ]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Use an asterisk "}<_components.code>{"*"}</_components.code>{" to match metrics with a particular label, regardless of the value. Conversely,\nto drop only metrics that don’t have a particular label, use "}<_components.code>{"!"}</_components.code>{" to specify a “does not equal”\nmatch, in conjunction with an asterisk "}<_components.code>{"*"}</_components.code>{" to match all values. For example, the following drop\nrule drops any metric containing the "}<_components.code>{"service"}</_components.code>{" label with a value of "}<_components.code>{"api"}</_components.code>{" that doesn’t also have\nan "}<_components.code>{"env"}</_components.code>{" label."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"A label with an empty value is the same as a label that isn’t present."}</_components.p></Alert>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"  \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"drop_rule"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api-drop-rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api drop rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"mode"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 1"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"    \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"filters"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"service\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"api\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"env!\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"        \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"*\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    ]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<Alert severity="info"><_components.p>{"Observability Platform glob syntax doesn’t support using two asterisks where one of\nthem is in the middle of a string. For example, "}<_components.code>{"*k8s*staging"}</_components.code>{" isn’t valid."}</_components.p></Alert>{"\n"}<_components.p>{"Select from the following methods to create a drop rule."}</_components.p>{"\n"}<InfoTerraformLockout />{"\n"}<Tabs><Tabs.Tab label="Web" id="create-an-always-active-drop-rule-ui"><_components.p>{"To create a drop rule, you must have administrative privileges:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"In the navigation menu, click "}<_components.strong><Icon path={mdiShieldAccount} />{" Go to Admin"}</_components.strong>{"\nand then select\n"}<_components.strong><Icon path={mdiShape} />{" Control "}<span aria-label="and then">{">"}</span>{" Drop Rules"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"+ Drop Rule"}</_components.strong>{"."}</_components.p>{"\n"}<_components.p>{"The drop rule consists of the following data model:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"A "}<_components.strong>{"Name"}</_components.strong>{" for the rule."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"A list of one or more "}<_components.strong>{"Matchers"}</_components.strong>{", which are labels (a "}<_components.strong>{"Key"}</_components.strong>{") with or without\na specific value. You can use\n"}<_components.a href="/investigate/querying/glob-syntax">{"glob syntax"}</_components.a>{" in the "}<_components.strong>{"Value"}</_components.strong>{" field."}</_components.p>{"\n"}<_components.p>{"To specify multiple values, separate values with a comma and surround them\nwith curly braces ("}<_components.code>{"{}"}</_components.code>{"). For example, "}<_components.code>{"key={value1,value2,value3}"}</_components.code>{"."}</_components.p>{"\n"}<_components.p>{"To match values containing a comma, specify the values without braces. For\nexample, "}<_components.code>{"key=value,continue"}</_components.code>{"."}</_components.p>{"\n"}<_components.p>{"When allowing only particular metrics, you might need a double negation, which\nrequires specific logical filtering:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="text" data-word-wrap=""><_components.code><_components.span><_components.span>{"keep: a==A && b==B"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"drop: !(a==A && b==B)"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"In combination, this logic leads to a drop rule where "}<_components.code>{"!a==A || !b==B --> a!=A || b!=B"}</_components.code>{"."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"Comma-separated lists can’t include spaces after commas. Rules with a\nspace after a comma don’t evaluate."}</_components.p></Alert>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Review"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Review the rule to ensure it’s correct."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Select the checkbox for "}<_components.strong>{"I have reviewed the proposed changes and they look\ncorrect"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Save"}</_components.strong>{" to finish, or "}<_components.strong>{"Back to Edit"}</_components.strong>{" to change the rule."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="create-drop-rule-chronoctl"><_components.p>{"To create a drop rule in "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{", define\nthe configured rule as a YAML resource definition and apply it."}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"To generate a resource based on a template, use the "}<_components.code>{"drop-rules scaffold"}</_components.code>{" command:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" drop-rules"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" scaffold"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"You can redirect this output to a file for editing:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" drop-rules"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" scaffold"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" >"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" drop-rule.yaml"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace "}<_components.code>{"drop-rule.yaml"}</_components.code>{" with any valid filename."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Configure the rule’s properties in the YAML resource definition. For example, this\nresource defines a drop rule named "}<_components.code>{"Drop node exporter"}</_components.code>{" with the slug\n"}<_components.code>{"drop-node-exporter-metrics"}</_components.code>{":"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" DropRule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Drop node exporter\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Unique identifier for rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"drop-node-exporter-metrics\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  filters"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" job"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" node_exporter"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine if rule is currently active"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  mode"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ENABLED"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  value_based_drop"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    enabled"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # The target data point value at which to drop metrics."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    target_drop_value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 75"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine whether to drop data points that are NaN."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  drop_nan_value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"true\""}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"To submit the configured drop rule to Observability Platform for creation,\nuse the "}<_components.code>{"apply"}</_components.code>{" command:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" drop-rule.yaml"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Terraform" id="create-an-always-active-drop-rule-terraform"><_components.p>{"Create an always active drop rule with Terraform by using the\n"}<_components.code>{"chronosphere_drop_rule"}</_components.code>{" type followed by a name in a resource declaration."}</_components.p><_components.p>{"For example, the following definition creates a drop rule that Terraform refers to as\n"}<_components.code>{"drop-node-exporter"}</_components.code>{", with the name "}<_components.code>{"Drop node exporter"}</_components.code>{". The rule drops all metrics\nfrom the "}<_components.code>{"node_exporter"}</_components.code>{" job."}</_components.p><_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_drop_rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"drop-node-exporter\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Name for the rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Drop node exporter\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Unique identifier for rule; generated automatically if left blank"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  slug = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"drop-node-exporter-metrics\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Label matcher to match metrics that should be dropped; all labels must match"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  query = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"job=node_exporter\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  ]"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine if rule is currently active"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  active = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine whether to drop data points that are NaN."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  drop_nan_value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre><_components.p>{"This rule is an example of a drop rule based on the value of the incoming data point:"}</_components.p><_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_drop_rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"value-based-drop-rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Name for the rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Drop Rule\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Label matcher to match metrics that should be dropped; all labels must match"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  query = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"ab=123\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  ]"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine ifrule is currently active"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  active = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Configuration of which metric values to drop; must be 0 or 1"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  value_based_drop "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    target_drop_value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"1"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine whether to drop data points if the values are NaN"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  drop_nan_value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"true"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{";"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre><TerraformCreate /></Tabs.Tab><Tabs.Tab label="API" id="create-drop-rule-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/CreateDropRule"><_components.code>{"CreateDropRule"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[4].id}>{toc[4].value}</_components.h2>{"\n"}<_components.p>{"Select from the following methods to disable a drop rule."}</_components.p>{"\n"}<InfoTerraformLockout />{"\n"}<Tabs><Tabs.Tab label="Web" id="disable-a-drop-rule-ui"><_components.p>{"To disable an existing drop rule in Observability Platform, you must have\nadministrative privileges:"}</_components.p><_components.ol>{"\n"}<_components.li>{"In the navigation menu, click "}<_components.strong><Icon path={mdiShieldAccount} />{" Go to Admin"}</_components.strong>{"\nand then select\n"}<_components.strong><Icon path={mdiShape} />{" Control "}<span aria-label="and then">{">"}</span>{" Drop Rules"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"Select the rule you want to disable from the list."}</_components.li>{"\n"}<_components.li>{"Toggle the rule to the "}<_components.strong>{"Disabled"}</_components.strong>{" state."}</_components.li>{"\n"}<_components.li>{"Click "}<_components.strong>{"Save"}</_components.strong>{"."}</_components.li>{"\n"}</_components.ol><_components.p>{"To re-enable the rule, use the previous steps and instead toggle "}<_components.strong>{"Always Active"}</_components.strong>{"."}</_components.p></Tabs.Tab><Tabs.Tab label="Chronoctl" id="disable-a-drop-rule-chronoctl"><_components.p>{"To disable a drop rule with "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"In the drop rule specification, set "}<_components.code>{"mode"}</_components.code>{" to "}<_components.code>{"DISABLED"}</_components.code>{"."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" DropRule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Drop node exporter\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Unique identifier for the rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"drop-node-exporter-metrics\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  filters"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" job"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    value_glob"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" node_exporter"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine if rule is currently active"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  mode"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" DISABLED"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  value_based_drop"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 75"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Submit the disabled drop rule definition to Observability Platform:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" FILE.yaml"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Terraform" id="disable-a-drop-rule-terraform"><_components.p>{"To disable a drop rule with "}<_components.a href="/tooling/infrastructure/terraform">{"Terraform"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Edit the drop rule’s definition in a Terraform file to set "}<_components.code>{"active"}</_components.code>{" to "}<_components.code>{"false"}</_components.code>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run the following command:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"terraform"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"For example, the following definition disables an always active drop rule that\nTerraform refers to as "}<_components.code>{"drop-node-exporter"}</_components.code>{", with a human-readable name of\n"}<_components.code>{"Drop node exporter"}</_components.code>{"."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"source "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_drop_rule\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"drop-node-exporter\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Name for the rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Drop node exporter\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Unique identifier for the rule"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  slug = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"drop-node-exporter\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Label matcher to match metrics that should be dropped; all labels must match"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  query = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"job=node_exporter\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  ]"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Boolean to determine if rule is currently active"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  active = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"false"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="API" id="disable-a-drop-rule-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/UpdateDropRule"><_components.code>{"UpdateDropRule"}</_components.code></_components.a>{" endpoint and update\nthe "}<_components.code>{"mode"}</_components.code>{" key to "}<_components.code>{"DISABLED"}</_components.code>{"."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[5].id}>{toc[5].value}</_components.h2>{"\n"}<_components.p>{"Select from the following methods to delete a drop rule."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="cloud-delete-drop-rule"><_components.ol>{"\n"}<_components.li>{"In the navigation menu select "}<_components.strong>{"Shaping "}<span aria-label="and then">{">"}</span>{" Drop Rules"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"Select the rule you want to delete from the list."}</_components.li>{"\n"}<_components.li>{"Click the "}<Icon path={mdiDelete} />{" delete icon to delete the rule."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="chronoctl-delete-drop-rule"><_components.p>{"To delete a drop rule using "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{", pass the rule’s\nslug to the "}<_components.code>{"drop-rules delete"}</_components.code>{" command:"}</_components.p><_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" drop-rules"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" delete"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" SLUG"}</_components.span></_components.span></_components.code></_components.pre><_components.p>{"Replace "}<_components.em><_components.code>{"SLUG"}</_components.code></_components.em>{" with the slug of the rule you want to delete."}</_components.p></Tabs.Tab><Tabs.Tab label="Terraform" id="terraform-delete-drop-rule"><_components.p>{"To delete a drop rule with Terraform:"}</_components.p><_components.ol>{"\n"}<_components.li>{"Remove its definition from the Terraform file."}</_components.li>{"\n"}<_components.li>{"Run "}<_components.code>{"terraform apply"}</_components.code>{"."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="API" id="api-delete-drop-rule"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/DeleteDropRule"><_components.code>{"DeleteDropRule"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs></>;
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/control/shaping/rules/drop-rules',
  {"filePath":"pages/control/shaping/rules/drop-rules.mdx",pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)